import type { Coupon, CouponStatus } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, cn } from '@mypoz/ui';

import { useFormat } from '@/lib/format.js';
import { CouponStatusChip } from './CouponStatusChip.js';

export interface CouponCardProps {
  coupon: Coupon;
  status: CouponStatus;
}

/**
 * One coupon, styled per the mentor mockup: face value and status chip share a row, then the
 * title, then a muted "code · until date" line. A used or expired coupon dims everything but the
 * status label itself — still legible, just clearly not something to reach for.
 *
 * The face value keeps two decimal places via `fmt.money`, same as every other price in the app
 * (the wallet balance, order totals) — a coupon is the one place a shorter "RM 10" without cents
 * would look tidier, but a special case here would make this screen the one place money is
 * formatted differently from everywhere else, which is worse.
 */
export function CouponCard({ coupon, status }: CouponCardProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const active = status === 'valid';

  return (
    <Card className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className={cn('text-2xl font-bold tabular-nums', active ? 'text-ink' : 'text-muted')}>
          {fmt.money(coupon.valueCents, coupon.currency)}
        </span>
        <CouponStatusChip status={status} />
      </div>

      <p className={cn('text-sm font-semibold', active ? 'text-ink' : 'text-muted')}>
        {coupon.title}
      </p>

      {/*
        The minimum spend, when the coupon carries one (MYP-73). Its own line rather than appended
        to the code-and-date line, because it is the one thing on this card that changes whether
        the face value above is reachable — "RM 20 off" on an order too small to qualify is the
        misreading this line exists to prevent.
      */}
      {coupon.minSpendCents === undefined ? null : (
        <p className={cn('text-xs', active ? 'text-muted' : 'text-muted-soft')}>
          {t('coupons.list.minSpend', {
            amount: fmt.money(coupon.minSpendCents, coupon.currency),
          })}
        </p>
      )}

      <p className="text-xs text-muted-soft">
        {/*
          A scheduled coupon leads with the date it opens, not the date it closes: "until 3 March"
          on something you cannot spend yet answers the wrong question.
        */}
        {status === 'scheduled'
          ? t('coupons.list.codeAndStart', { code: coupon.code, date: fmt.date(coupon.startDate) })
          : t('coupons.list.codeAndExpiry', { code: coupon.code, date: fmt.date(coupon.endDate) })}
      </p>
    </Card>
  );
}
